import { formatDuration, truncate, formatDate, formatGameName } from '../utils/format';
import { trackClipSelected } from '../services/analytics';

/**
 * Single clip card component
 */
function ClipCard({ clip, isSelected, onToggle, canSelect }) {
  const handleClick = () => {
    if (isSelected || canSelect) {
      onToggle(clip.id);
      trackClipSelected(clip.id, clip.title);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`
        relative group cursor-pointer rounded-lg overflow-hidden
        transition-all duration-200 ease-out
        ${isSelected 
          ? 'ring-2 ring-primary ring-offset-2 ring-offset-dark-900 scale-[0.98]' 
          : 'hover:ring-1 hover:ring-dark-500'
        }
        ${!canSelect && !isSelected ? 'opacity-50 cursor-not-allowed' : ''}
      `}
      aria-pressed={isSelected}
      aria-label={`${isSelected ? 'Deselect' : 'Select'} clip: ${clip.title}`}
    >
      {/* Thumbnail with 16:9 aspect ratio */}
      <div className="relative aspect-video bg-dark-700">
        <img
          src={clip.thumbnailUrl}
          alt={clip.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />

        {/* Duration badge */}
        <span className="absolute bottom-2 right-2 px-1.5 py-0.5 text-xs font-medium bg-black/80 rounded-sm" aria-label={`Duration: ${formatDuration(clip.duration)}`}>
          {formatDuration(clip.duration)}
        </span>

        {/* Selection indicator */}
        <div
          className={`
            absolute top-2 left-2 w-6 h-6 rounded-full border-2 
            flex items-center justify-center transition-all
            ${isSelected 
              ? 'bg-primary border-primary' 
              : 'bg-black/50 border-white/50 group-hover:border-white'
            }
          `}
          aria-hidden="true"
        >
          {isSelected && (
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
      </div>

      {/* Clip info */}
      <div className="p-3 bg-dark-800">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-medium text-sm leading-tight line-clamp-2 flex-shrink" title={clip.title}>
            {truncate(clip.title, 60)}
          </h3>
          {/* Authorship badge */}
          {clip.isCreatorAdded && (
            <span
              className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-green-900/40 text-green-300 rounded-sm text-xs font-medium whitespace-nowrap"
              title="You are the original creator of this clip"
            >
              <span aria-hidden="true">👑</span>
              <span className="hidden sm:inline">Creator</span>
              <span className="sm:hidden" aria-label="Creator">C</span>
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-col gap-1">
          <p className="text-xs text-gray-400">
            {clip.broadcasterName}
          </p>
          {clip.creatorName && !clip.isCreatorAdded && (
            <p className="text-xs text-gray-500">
              Clipped by {clip.creatorName}
            </p>
          )}
          {clip.createdAt && (
            <p className="text-xs text-gray-500">
              on {formatDate(clip.createdAt, 'date')}
            </p>
          )}
          {clip.gameName && (
            <span className="text-xs bg-purple-900/40 text-purple-300 px-2 py-0.5 rounded-sm w-fit">
              <span aria-hidden="true" className="mr-1">🎮</span>
              {formatGameName(clip.gameName)}
            </span>
          )}
          <div className="text-xs text-gray-500">
            {clip.viewCount.toLocaleString()} views
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * ClipGrid - Responsive grid of selectable clips
 */
export default function ClipGrid({
  clips = [],
  selectedIds = [],
  onToggle,
  maxSelection = 5,
  loading = false,
}) {
  const selectedSet = new Set(selectedIds);
  const canSelectMore = selectedSet.size < maxSelection;

  if (loading && clips.length === 0) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" role="status" aria-label="Loading clips">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="animate-pulse" aria-hidden="true">
            <div className="aspect-video bg-dark-700 rounded-lg" />
            <div className="mt-2 h-4 bg-dark-700 rounded-sm w-3/4" />
            <div className="mt-1 h-3 bg-dark-700 rounded-sm w-1/2" />
          </div>
        ))}
        <span className="sr-only">Loading clips...</span>
      </div>
    );
  }

  if (clips.length === 0) {
    return (
      <div className="text-center py-12" role="status">
        <svg className="mx-auto h-12 w-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        <h3 className="mt-4 text-lg font-medium text-gray-300">No clips found</h3>
        <p className="mt-1 text-gray-500">🎞️🎞️🎞️🎞️🎞️</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {clips.map((clip) => (
        <ClipCard
          key={clip.id}
          clip={clip}
          isSelected={selectedSet.has(clip.id)}
          onToggle={onToggle}
          canSelect={canSelectMore || selectedSet.has(clip.id)}
        />
      ))}
    </div>
  );
}
